import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, Container } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';
import { useNotices, isBannerDismissedToday, setBannerDismissedToday } from '../../hooks/useNotices';
import { priorityStyles } from './noticePriorityStyles';
import NoticeDetailDialog from './NoticeDetailDialog';
import type { Notice } from '../../../types/notice.types';

const MotionBox = motion(Box);

const UrgentBanner: React.FC = () => {
  const { bannerNotices } = useNotices();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);

  useEffect(() => {
    const dismissedSet = new Set<string>();
    bannerNotices.forEach((n) => {
      if (isBannerDismissedToday(n.id)) dismissedSet.add(n.id);
    });
    setDismissed(dismissedSet);
  }, [bannerNotices]);

  const visibleBanners = bannerNotices.filter((n) => !dismissed.has(n.id));

  useEffect(() => {
    if (visibleBanners.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % visibleBanners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [visibleBanners.length]);

  useEffect(() => {
    if (currentIndex >= visibleBanners.length) setCurrentIndex(0);
  }, [visibleBanners.length, currentIndex]);

  const handleDismiss = useCallback((e: React.MouseEvent, notice: Notice) => {
    e.stopPropagation();
    setBannerDismissedToday(notice.id);
    setDismissed((prev) => new Set(prev).add(notice.id));
  }, []);

  const handleBannerClick = (notice: Notice) => {
    setSelectedNotice(notice);
    setDetailOpen(true);
  };

  if (visibleBanners.length === 0) return null;

  const current = visibleBanners[currentIndex % visibleBanners.length];
  if (!current) return null;

  const style = priorityStyles[current.priority];

  return (
    <>
      <AnimatePresence mode="wait">
        <MotionBox
          key={current.id}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          sx={{ overflow: 'hidden' }}
        >
          <Box
            onClick={() => handleBannerClick(current)}
            sx={{
              background: style.gradient,
              borderBottom: `2px solid ${style.borderColor}`,
              cursor: 'pointer',
              '&:hover': { filter: 'brightness(0.97)' },
            }}
          >
            <Container maxWidth="lg">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: { xs: 1.2, md: 1.5 },
                  px: { xs: 1, md: 2 },
                  gap: { xs: 1, md: 2 },
                  minHeight: 48,
                }}
              >
                {current.priority === 'urgent' && (
                  <Box
                    sx={{
                      width: 8, height: 8, borderRadius: '50%', bgcolor: style.dotColor, flexShrink: 0,
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.5, transform: 'scale(1.5)' },
                      },
                    }}
                  />
                )}

                <Box sx={{ px: 1, py: 0.3, borderRadius: 1, bgcolor: style.color, color: '#fff', fontSize: { xs: 10, md: 11 }, fontWeight: 800, flexShrink: 0, lineHeight: 1.4 }}>
                  {style.label}
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: 13, md: 14 }, fontWeight: 600, color: style.color,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    flex: 1, textAlign: 'center',
                  }}
                >
                  {current.title}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: 11, md: 12 }, fontWeight: 700, color: style.color,
                    textDecoration: 'underline', flexShrink: 0, opacity: 0.7,
                  }}
                >
                  자세히 보기
                </Typography>

                {visibleBanners.length > 1 && (
                  <Typography sx={{ fontSize: 11, color: style.color, opacity: 0.6, flexShrink: 0 }}>
                    {currentIndex + 1}/{visibleBanners.length}
                  </Typography>
                )}

                <IconButton
                  size="small"
                  onClick={(e) => handleDismiss(e, current)}
                  sx={{ color: style.color, opacity: 0.6, '&:hover': { opacity: 1 }, p: 0.3, flexShrink: 0 }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Container>
          </Box>
        </MotionBox>
      </AnimatePresence>

      <NoticeDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        notice={selectedNotice}
      />
    </>
  );
};

export default UrgentBanner;
